<script setup lang="ts">
import {computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref} from "vue";
import {storeToRefs} from "pinia";
import type {MenuItemInterface} from "~~/server/types/menu";
import {menuPanels} from "~/components/menu/panels";

const MobileMenu = defineAsyncComponent(() => import("~/components/menu/mobile_menu.vue"));
const FastSearchComponent = defineAsyncComponent(() => import("~/components/products/fastSearchComponent.vue"));
const AnnouncementComponent = defineAsyncComponent(() => import("~/components/header/AnnouncementComponent.vue"));


const settingsStore=useSettingsStore();
const authStore = useAuthStore();
const menusStore = useMenusStore();
const cartStore = useCartStore();
const favoriteStore=useFavoritesStore();
const localePath = useLocalePath();
const switchLocalePath = useSwitchLocalePath();
const { locale } = useI18n();
const { _settings:settings } = storeToRefs(settingsStore);
const { isAuthenticated, initials, isAdmin } = storeToRefs(authStore)
const { menu: menus } = storeToRefs(menusStore);
const menuItems = computed(() => menus.value?.find(i => i.position === "main")?.menu_items || []);
const favorites=computed(()=>favoriteStore.favorites)

const route=useRoute()
const items = computed<MenuItemInterface[]>(() => menuItems.value.map((item: any) => ({
            name: item.name,
            url: item.url,
            icon: item.icon ,
            design: item.design,
            children:
                item.custom_data?.items?.map((i: any) => ({
                    name: i.name,
                    url: i.url,
                    icon: i.icon,
                    children:
                        i?.items?.map((j: any) => ({
                            name: j.name,
                            url: j.url,
                            icon: j.icon,
                        })) || [],
                })) || [],
        })) as MenuItemInterface[]);
const cart_items = computed(() => cartStore.cart?.products?.length ?? 0)
const siteLanguages = computed(() => settings.value?.languages || []);
const hideFastSearch = computed(() => settings.value?.hide_fast_search === 1);
const hideFavoriteIcon = computed(() => settings.value?.hide_favorite_icon === 1);
const hideLoginIcon = computed(() => settings.value?.hide_login_icon === 1);

const mobileOpen = ref(false);
const openIndex = ref<number | null>();

const getPanel = (design?: string) => (design ? menuPanels[design] : null);
const { isDesktopOrTablet } = useDevice()

onMounted(() => {
    show_search.value = isDesktopOrTablet
    document.addEventListener("keydown", onKeydown);
});

onBeforeUnmount(() => {
    document.removeEventListener("keydown", onKeydown);
});

const onKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
        setOpen(null, 0);
        mobileOpen.value = false;
    }
};

let hoverTimer: number | null = null;
const setOpen = (idx: number | null, delay = 90) => {
    //openIndex.value=2; return;
    if (hoverTimer) window.clearTimeout(hoverTimer);
     hoverTimer = window.setTimeout(() => (openIndex.value = idx), delay);
};

const onUserCommand = async (cmd: string) => {
    if (cmd === "login") return navigateTo(localePath("/login"));
    if (cmd === "register") return navigateTo(localePath("/register"));
  if (cmd === "profile") return navigateTo(localePath("/profile"));
  if (cmd === "admin") return window.location.href = '/admin';
    if (cmd === "favorites") return navigateTo(localePath("/favorites"));
    if (cmd === "logout") {
        await authStore.logout();
        await navigateTo(localePath("/"));
    }
};
const switchLanguage = (language: string) => {
    if (language !== locale.value) {
        window.location.assign(switchLocalePath(language as any));
    }
};
const show_search = ref(false)
const hide_header=ref(false);

const openSearch = () => {
    if (hideFastSearch.value) return;
    show_search.value=true;
    hide_header.value=true;
}
const closeSearch = () => {
    show_search.value=false;
    hide_header.value=false;
}
watch(()=>route.fullPath,()=>openIndex.value=null);
</script>

<template>
  <client-only>
  <announcement-component
      :text="settings?.announcement_texts?.[locale]"
      :enabled="settings?.announcement_enabled === true"
      :animated="settings?.announcement_animation === true"
      :color="settings?.announcement_color"
      :background="settings?.announcement_bg"
  />
  </client-only>
  <header class="container-xxl ">
        <div class="header" :class="{ 'header--search-hidden': hideFastSearch }">
            <ClientOnly>
                <div class="mobile_btn d-lg-none d-flex">
                <el-button @click="mobileOpen = true"  aria-label="Menu" link>
                    <Icon name="material-symbols-light:menu" size="34" />
                </el-button>
                <el-button class="m-0" v-if="!hideFastSearch && !show_search" @click="openSearch" aria-label="Search" link>
                    <Icon name="material-symbols-light:search" size="34" />
                </el-button>
            </div>
            </ClientOnly>
            <div v-if="!hide_header"
                 role="banner"
                 :style="{
                  '--logo-width': settings?.logo_width,
                   '--logo-height': settings?.logo_height,
                   '--logo-mobile-width': settings?.logo_mobile_width,
                   '--logo-mobile-height': settings?.logo_mobile_height }"
                 class="logo"  >
                <NuxtLinkLocale to="/">
                  <picture>
                    <source media="(min-width: 700px)" :srcset="settings?.logo" />
                    <source media="(max-width: 699px)" :srcset="settings?.logo_mobile || settings?.logo" />
                    <img
                      class="brand"
                      :src="settings?.logo"
                      :alt="settings?.site_title || 'Logo'"
                      loading="eager"
                      decoding="async"
                      :width="settings?.logo_width"
                      :height="settings?.logo_height"
                    />
                  </picture>
                </NuxtLinkLocale>
            </div>
            <ClientOnly>
                <div class="search" v-if="!hideFastSearch && show_search">
                    <fast-search-component />
                </div>
                <div class="actions">
                    <template  v-if="!hide_header">
                    <el-dropdown
                        v-if="siteLanguages.length > 1"
                        trigger="click"
                        @command="switchLanguage"
                    >
                        <el-button link :aria-label="$t('language')" style="--el-button-text-color:#000000">
                            <span class="language-code">{{ locale.toUpperCase() }}</span>
                        </el-button>
                        <template #dropdown>
                            <el-dropdown-menu>
                                <el-dropdown-item
                                    v-for="language in siteLanguages"
                                    :key="language.code"
                                    :command="language.code"
                                    :disabled="language.code === locale"
                                >
                                    <img :src="language.flag" alt="" class="language-flag" />
                                    {{ language.name }}
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>
                    <el-dropdown v-if="!hideLoginIcon" trigger="click" popper-class="user-dd" @command="onUserCommand" popperStyle="--el-border-color-light:var(--el-color-primary)">
                        <template #default>
                            <el-button link aria-label="User menu" style="--el-button-text-color:#000000">
                                <el-badge v-if="isAuthenticated" :value="initials"  type="primary" class="fw-bold" >
                                    <Icon name="lets-icons:user-light" size="34" />
                                </el-badge>
                                <Icon v-else name="lets-icons:user-light" size="34" />
                            </el-button>
                        </template>
                        <template #dropdown>
                            <el-dropdown-menu >
                                <template v-if="!isAuthenticated">
                                    <el-dropdown-item command="login">{{ $t("login") }}</el-dropdown-item>
                                    <el-dropdown-item command="register">{{ $t("register") }}</el-dropdown-item>
                                </template>

                                <template v-else>
                                  <el-dropdown-item command="favorites">{{ $t("favorites") }}</el-dropdown-item>
                                  <el-dropdown-item command="profile">{{ $t("profile") }}</el-dropdown-item>
                                  <el-dropdown-item v-if="isAdmin" command="admin" divided>{{ $t("administration") }}</el-dropdown-item>
                                  <el-dropdown-item divided command="logout">{{ $t("logout") }}</el-dropdown-item>
                                </template>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>

                      <NuxtLinkLocale
                          v-if="!hideFavoriteIcon"
                          to="/favorites"
                                aria-label="favorites"
                                class="d-none d-md-block"
                        >
                            <el-badge :value="favorites.length"  type="primary" class="fw-bold">
                                <Icon name="lets-icons:favorite-light" size="34" />
                            </el-badge>
                        </NuxtLinkLocale>
                      <NuxtLinkLocale
                          v-if="settings.enable_add_to_cart"
                          to="/cart"
                          aria-label="cart"
                          class="m-0"
                      >
                        <el-badge :value="cart_items" type="primary" class="fw-bold">
                          <Icon name="lets-icons:basket-alt-3-light" size="34" />
                        </el-badge>
                      </NuxtLinkLocale>


                    </template>
                    <el-button v-else @click="closeSearch"  aria-label="back" link>
                        <Icon name="material-symbols-light:home-outline" size="34" />
                    </el-button>
                </div>
            </ClientOnly>
        </div>
    </header>
   <nav class="menu mb-lg-3" role="navigation">
        <div class="container-xxl">
            <ul class="menu-list">
                <li
                        v-for="(item, idx) in items"
                        :key="idx"
                        @mouseenter="setOpen(idx)"
                        @mouseleave="setOpen(null, 0)"
                        @focusin="setOpen(idx)"
                        @focusout="setOpen(null, 0)"
                        :class="item.design"
                >
                    <NuxtLinkLocale
                            :to="item.url"
                            class="menu-link"
                            :aria-haspopup="!!item.children?.length"
                            :aria-expanded="openIndex === idx"
                            :aria-controls="item.children?.length ? `panel-${idx}` : undefined"
                    >
                        <span>{{ item.name }}</span>
                        <span v-if="item.children?.length" class="menu-caret">▾</span>
                    </NuxtLinkLocale>

                    <transition name="scale-fade">
                        <component
                                v-if="openIndex === idx && getPanel(item.design)"
                                :is="getPanel(item.design)"
                                :item="item"
                                :idx="idx"
                        />
                    </transition>
                </li>
            </ul>
        </div>
    </nav>
    <MobileMenu v-if="mobileOpen" :items="items" :settings="settings" v-model:mobileOpen="mobileOpen" />
</template>

<style scoped lang="scss">
ul{
  margin: 0;
  padding: 0;
  list-style: none;
}
.header {
  display: grid;
  grid-template-rows: minmax(50px, auto);
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  grid-template-areas: "logo search actions";
  padding: 0.5em 0;
  @media screen and (max-width: 992px) {
    grid-template-columns: 1fr auto 0 1fr;
    grid-template-areas:"mobile_btn logo search actions";
    padding: 0.5em 0;
  }
  @media screen and (min-width: 993px) {
    &.header--search-hidden {
      grid-template-areas: ". logo actions";

      .logo {
        justify-self: center;
        width: auto;
      }
    }
  }

  .logo {
    grid-area: logo;
    justify-self: start;
    width: 100%;
    height: auto;
    @media screen and (max-width: 992px) {
      justify-self: center;
    }
    .brand {
      display: block;
      max-width: calc(var(--logo-width) * 1px);
      max-height: calc(var(--logo-height) * 1px);
      aspect-ratio: var(--logo-width) / var(--logo-height);
      @media screen and (max-width: 992px) {
        max-width: calc(var(--logo-mobile-width) * 1px);
        max-height: calc(var(--logo-mobile-height) * 1px);
        aspect-ratio: var(--logo-mobile-width) / var(--logo-mobile-height);
      }
    }
  }
  .search {
    grid-area: search;
    justify-self: center;
  }

  .actions {
    grid-area: actions;
    justify-self: end;
  }
  .mobile_btn {
    grid-area: mobile_btn;
    justify-self: start;
  }
  .actions,.mobile_btn {
    display: flex;
    align-items: center;
    gap: 10px;
    @media screen and (max-width: 992px) {
      gap: 0;
    }
    min-height: 50px;
  }
}

.language-code {
  font-weight: 600;
  font-size: 1rem;
}

.language-flag {
  width: 18px;
  height: 12px;
  margin-right: 0.5rem;
  object-fit: cover;
}

.menu {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--nav-bg);
  color: var(--nav-text);

  .container {
    position: relative;
  }

  .menu-list {
    display: flex;
    position: relative;
    justify-content: space-between;


    > li {
      white-space: nowrap;
      padding:5px;
      position: static;
      font-size: var(--fs-0);

      &:first-child {
        padding-left: 0;
      }
      &:last-child {
        padding-right: 0;
      }

      &.custom_dropdown {
        position: relative;
      }

      .menu-link {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: inherit;
        text-decoration: none;
        position: relative;

        .menu-caret {
          display: inline-block;
          line-height: 1;
          vertical-align: middle;
          font-size: var(--fs--1);
          transform-origin: 50% 55%;
          --caret-nudge: 0.02em;
          transform: translateY(var(--caret-nudge));
          transition: transform 0.2s ease;
          backface-visibility: hidden;
          will-change: transform;
        }
      }

      &:hover,
      &:focus-within {
        background-color: transparent;

        .menu-link .menu-caret {
          transform: rotate(180deg) translateY(var(--caret-nudge));
        }
        .menu-link {
          color: var(--nav-text-hover);
        }
      }
    }
  }

  /* =========================
     PANEL BASE (ALL TYPES)
     (важно: тези са общи за child компонентите)
  ========================= */
  .panel {
    z-index: 100;
    position: absolute;
    top: calc(100%);
    min-width: 180px;
    background: var(--nav-panel-bg);
    color: var(--nav-panel-text);

    box-shadow: 0 2px 6px 0 rgb(0 0 0 / 0%), 0 2px 5px 0 rgb(0 0 0 / 30%);
    padding: 10px;
  }

  .panel-full {
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
  }

  /* =========================
     PANEL HEADER (common)
  ========================= */
  .panel-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 6px 14px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    .panel-title {
      font: 700 1.05rem/1.1 system-ui, sans-serif;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    .panel-view_all {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font: 600 14px/1 system-ui, sans-serif;
      opacity: 0.9;

      &:hover {
        text-decoration: underline;
        opacity: 1;
      }
    }
  }

  /* =========================
     PANEL ITEMS BASE (common)
  ========================= */
  .panel-items {
    margin-top: 14px;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 20px;

    @media screen and (max-width: 992px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .item {
      .level_1 {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;

        .title {
          font-size: var(--fs-0);
          text-transform: uppercase;
          letter-spacing: 0.04em;

          &:hover {
            color: var(--nav-panel-text-hover);
          }
        }

        .image {
          width: 60px;
          opacity: 0.95;
        }
      }

      .level_2 {
        margin-top: 10px;

        li {
          padding: 2px 0;
        }

        .title {
          opacity: 0.92;

          &:hover {
            color: var(--nav-panel-text-hover);
            opacity: 1;
          }
        }
      }
    }
  }
}

/* =========================
   TRANSITION
   (трябва да е global, за да работи и за child компонентите)
========================= */
.scale-fade-enter-from,
.scale-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}
.scale-fade-enter-active,
.scale-fade-leave-active {
  transition: transform 0.16s ease, opacity 0.16s ease;
}
.scale-fade-enter-to,
.scale-fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* показваме бургер и крием десктоп меню под 992px */
@media (max-width: 991.98px) {
  .menu {
    display: none;
  }
}
</style>
